import React, { useState, useEffect } from "react";
import { X, Save, ChevronLeft, ChevronRight } from "lucide-react";
import SummaryApi from "../common";
import fetchWithAuth from "../common/fetchWithAuth";
import { toast } from "react-toastify";
export default function AttendanceModal({ staff, onClose, onSuccess }) {
    const [status, setStatus] = useState("present");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    // Calendar State
    const [currentDate, setCurrentDate] = useState(new Date());
    const [attendanceData, setAttendanceData] = useState({});
    const [loadingHistory, setLoadingHistory] = useState(false);

    const fetchAttendance = async () => {
        try {
            setLoadingHistory(true);
            const month = currentDate.getMonth() + 1;
            const year = currentDate.getFullYear();
            const url = `${SummaryApi.getStaffAttendance.url(staff._id)}?month=${month}&year=${year}`;

            const res = await fetchWithAuth(url, {
                method: SummaryApi.getStaffAttendance.method,
            });

            const data = await res.json();
            if (data.success) {
                const activeData = {};
                data.data.forEach(record => {
                    const d = new Date(record.date);
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    const dateKey = `${year}-${month}-${day}`;
                    activeData[dateKey] = record.status.toLowerCase();
                });
                setAttendanceData(activeData);
            }
        } catch (err) {
            console.error("Error fetching attendance:", err);
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        if (staff?._id) {
            fetchAttendance();
        }
    }, [staff, currentDate]);

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const days = new Date(year, month + 1, 0).getDate();
        const firstDay = new Date(year, month, 1).getDay();
        return { days, firstDay };
    };

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const submit = async () => {
        try {
            setSaving(true);
            const response = await fetchWithAuth(SummaryApi.markAttendance.url, {
                method: SummaryApi.markAttendance.method,
                body: JSON.stringify({
                    staffId: staff._id,
                    date: new Date(), // Marking for today
                    status,
                    note
                })
            });

            const data = await response.json();

            if (data.success) {
                // If marking for current month, refresh calendar
                if (new Date().getMonth() === currentDate.getMonth()) {
                    await fetchAttendance();
                }
                toast.success(`Attendance marked successfully!`);
                if (onSuccess) onSuccess();
                // onClose();
            } else {
                alert(data.message || "Failed to mark attendance");
            }
        } catch (err) {
            console.error(err);
            alert("Failed to save attendance");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl border border-gray-100 flex flex-col md:flex-row overflow-hidden max-h-[90vh] relative">

                {/* Left Side: Mark Attendance Form */}
                <div className="p-6 md:w-1/3 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-800">Mark Attendance</h3>
                        <p className="text-sm text-emerald-600 font-medium">{staff.name}</p>
                    </div>

                    <div className="space-y-4 flex-1">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Date</label>
                            <div className="text-gray-900 font-medium">{new Date().toLocaleDateString()}</div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                            >
                                <option value="present">Present (Full Day)</option>
                                <option value="absent">Absent</option>
                                <option value="half">Half-day</option>
                            </select>
                        </div>

                        {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Note (optional)</label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all resize-none"
                                rows="3"
                                placeholder="Reason..."
                            />
                        </div> */}
                    </div>

                    <div className="mt-4 flex gap-3">
                        <button
                            onClick={submit}
                            disabled={saving}
                            className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 font-bold transition-all flex items-center justify-center gap-2"
                        >
                            {saving ? "Saving..." : <><Save size={18} /> Save</>}
                        </button>
                    </div>
                </div>

                {/* Right Side: Calendar View */}
                <div className="p-6 md:w-2/3 bg-gray-50 flex flex-col">
                    <div className="flex items-center justify-between mb-6">
                        <h4 className="font-bold text-gray-800">Attendance History</h4>
                        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200 transition-colors md:hidden">
                            <X size={20} />
                        </button>
                        <button onClick={onClose} className="hidden md:block p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200 transition-colors absolute top-4 right-4">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex-1 flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <span className="font-bold text-gray-700">
                                {currentDate.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
                            </span>
                            <div className="flex items-center gap-2">
                                <button onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">
                                    <ChevronLeft size={16} className="text-gray-500" />
                                </button>
                                <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">
                                    <ChevronRight size={16} className="text-gray-500" />
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-7 gap-1 text-center mb-2 border-b border-gray-100 pb-2">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                                <div key={d} className="text-xs font-bold text-gray-400 uppercase tracking-wider">{d}</div>
                            ))}
                        </div>

                        <div className="grid grid-cols-7 gap-1 auto-rows-fr flex-1">
                            {Array.from({ length: getDaysInMonth(currentDate).firstDay }).map((_, i) => (
                                <div key={`empty-${i}`}></div>
                            ))}

                            {Array.from({ length: getDaysInMonth(currentDate).days }).map((_, i) => {
                                const day = i + 1;
                                const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const status = attendanceData[dateKey];

                                let bgClass = "bg-gray-50 text-gray-400 hover:bg-gray-100";
                                if (status === 'present') bgClass = "bg-emerald-100 text-emerald-700 font-bold border border-emerald-200";
                                if (status === 'absent') bgClass = "bg-rose-100 text-rose-700 font-bold border border-rose-200";
                                if (status === 'half') bgClass = "bg-amber-100 text-amber-700 font-bold border border-amber-200";

                                return (
                                    <div
                                        key={day}
                                        className={`min-h-[2.5rem] flex items-center justify-center rounded-lg text-sm transition-all ${bgClass}`}
                                        title={status ? `Status: ${status}` : "No Record"}
                                    >
                                        {day}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex gap-4 justify-center mt-4 text-xs text-gray-500 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div> Present
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div> Half Day
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-rose-500"></div> Absent
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}